import React, { useState } from "react";
import { Box, Typography, Button, Stack, Paper } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

interface BoletoPaymentViewProps {
  identificationField: string;
  bankSlipUrl: string;
  onBack?: () => void;
}

const formatLinhaDigitavel = (field: string): string => {
  if (!field || field.length < 30) return field || "";
  return `${field.substring(0, 5)}.${field.substring(5, 10)} ` +
         `${field.substring(10, 15)}.${field.substring(15, 21)} ` +
         `${field.substring(21, 26)}.${field.substring(26, 32)} ` +
         `${field.substring(32, 33)} ${field.substring(33)}`;
};

const BoletoPaymentView: React.FC<BoletoPaymentViewProps> = ({ identificationField, bankSlipUrl, onBack }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(identificationField);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "var(--ink)" }}>
        Boleto Bancário
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: "var(--surface-2)",
          border: "1px solid var(--border)",
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" sx={{ color: "var(--ink-soft)",  mb: 0.5, display: "block" }}>
          Linha Digitável
        </Typography>
        <Typography
          fontFamily="monospace"
          fontSize="0.95rem"
          fontWeight={600}
          sx={{
            wordBreak: "break-all",
            lineHeight: 1.6,
            color: "var(--ink)",
            letterSpacing: "0.5px",
          }}
        >
          {formatLinhaDigitavel(identificationField)}
        </Typography>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} justifyContent="center">
        <Button
          variant="contained"
          onClick={handleCopy}
          startIcon={<ContentCopyIcon />}
          sx={{
            bgcolor: copied ? "var(--green)" : "var(--blue)",
            "&:hover": { bgcolor: copied ? "var(--green)" : "var(--blue-hover)", filter: "brightness(0.93)" },
          }}
        >
          {copied ? "Copiado!" : "Copiar Linha Digitável"}
        </Button>
        <Button
          variant="outlined"
          href={bankSlipUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<PictureAsPdfIcon />}
        >
          Visualizar Boleto em PDF
        </Button>
      </Stack>

      <Stack spacing={1} sx={{ p: 2, bgcolor: "var(--orange-tint)", borderRadius: 2, border: "1px solid var(--orange)" }}>
        <Typography variant="body2" sx={{ color: "var(--ink-soft)" }}>
          ⏳ O boleto pode levar até 3 dias úteis para compensar.
        </Typography>
        <Typography variant="body2" sx={{ color: "var(--ink-soft)" }}>
          Após a confirmação do pagamento, sua assinatura será ativada automaticamente.
        </Typography>
      </Stack>

      {onBack && (
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            mt: 3,
            color: "var(--ink-soft)",
            borderColor: "var(--border-strong)",
            textTransform: "none",
            "&:hover": { borderColor: "var(--ink-faint)", bgcolor: "var(--surface-2)" },
          }}
        >
          Voltar / Alterar Forma de Pagamento
        </Button>
      )}
    </Box>
  );
};

export default BoletoPaymentView;
